import { IFrequentlyAskedQuestion } from 'types/cms';

import {
  EXACT_MATCH_WEIGHT,
  buildTagsFromCategoryFields,
  expandSynonyms,
  matchWeight,
} from './prioritiseByContext';

export const MAX_FAQ_ITEMS = 6;

export type FaqContext = {
  grade?: string;
  speciality?: string;
  rootSpeciality?: string;
  location?: string;
};

// The large majority of Medrecruit's jobs/traffic is AU-based - when a search has no location
// filter at all, the location dimension scores against this default (rather than contributing
// nothing) so AU-relevant FAQs lean towards the top.
const DEFAULT_LOCATION_TERM = 'Australia';

// Weight invariant: the two bonuses a FAQ can earn *without* genuinely matching the searched
// grade/speciality/location - the synthetic default-location match and recency - must always sum
// to strictly less than a single genuine match (EXACT_MATCH_WEIGHT, 1), so they can break ties
// but can never tie or outrank a FAQ that genuinely matches. The default location is therefore a
// small fraction of a real match, not a full one: at full weight, an AU-tagged FAQ that was also
// recently updated (1 + recency) would beat an older FAQ that genuinely matches the searched
// grade. Today: 0.1 + 0.1 = 0.2 < 1. Mirrors the guides ranking (filterGuidesForContext).
const DEFAULT_LOCATION_WEIGHT = 0.1;
const RECENCY_BONUS_WEIGHT = 0.1;
const RECENCY_WINDOW_DAYS = 90;
const RECENCY_WINDOW_MS = RECENCY_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const isRecentlyUpdated = (updatedAt: string | undefined, now: number): boolean => {
  if (!updatedAt) {
    return false;
  }

  const updatedAtMs = new Date(updatedAt).getTime();
  if (Number.isNaN(updatedAtMs)) {
    return false;
  }

  // A future-dated updated_at (bad CMS data, or clock skew) must not count as recent - without the
  // `>= 0` check, its (negative) age would still satisfy `<= RECENCY_WINDOW_MS` and grant the bonus
  // until the real date catches up, contradicting "updated within the last 90 days".
  const ageMs = now - updatedAtMs;
  return ageMs >= 0 && ageMs <= RECENCY_WINDOW_MS;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// `\b` requires the variant to be bounded by non-word characters (or the string's edges), so a
// short abbreviation like "au" matches the standalone word "AU" but not the "au" inside "Auckland"
// - plain substring containment would otherwise treat any NZ-city question mentioning Auckland as
// an Australia match too (matchWeight avoids this for tags via exact-value equality; this is the
// free-text equivalent of that same guarantee).
const containsWholeWordOrPhrase = (text: string, term: string): boolean =>
  new RegExp(`\\b${escapeRegExp(term)}\\b`).test(text);

// A FAQ's `question` is free text rather than an editor-assigned tag, so a term matches it via
// whole-word/phrase containment (through the same synonym table tag-matching uses) rather than the
// exact-token equality matchWeight applies to category/subcategory - "Registrar" should match
// "What's the pay rate for a Registrar in Sydney?" without that FAQ needing a matching tag too.
const questionMatchesTerm = (question: string, term: string): boolean => {
  const normalizedQuestion = question.toLowerCase();
  return expandSynonyms(term).some(variant =>
    containsWholeWordOrPhrase(normalizedQuestion, variant),
  );
};

/**
 * Scores how well a single FAQ matches a single context term: the best of an exact/synonym tag
 * match, a broader country-level fallback match (see matchWeight), or the term appearing in the
 * FAQ's question text - the last of these at the same weight as an exact tag match, since editors
 * only reliably tag a minority of entries and the question text carries an equivalent signal.
 * Deliberately the *best* of these, not their sum - a term matching both a tag and the question
 * still only counts once towards this term's contribution to the FAQ's total score.
 */
const scoreTermAgainstFaq = (faq: IFrequentlyAskedQuestion, term: string): number => {
  const tags = buildTagsFromCategoryFields(faq.category, faq.subcategory);
  const tagScore = Math.max(0, ...tags.map(tag => matchWeight(tag, term)));
  const questionScore = questionMatchesTerm(faq.question, term) ? EXACT_MATCH_WEIGHT : 0;

  return Math.max(tagScore, questionScore);
};

type WeightedContextTerm = { term: string; weight: number };

const scoreFaq = (
  faq: IFrequentlyAskedQuestion,
  contextTerms: WeightedContextTerm[],
  now: number,
): number => {
  const contextScore = contextTerms.reduce(
    (total, { term, weight }) => total + weight * scoreTermAgainstFaq(faq, term),
    0,
  );
  const recencyScore = isRecentlyUpdated(faq.updated_at, now) ? RECENCY_BONUS_WEIGHT : 0;

  return contextScore + recencyScore;
};

/**
 * Selects which CMS FAQs to show for the current grade/speciality/location search context.
 * Every FAQ is scored and re-ordered highest-first, none are dropped for scoring 0 - real CMS
 * content isn't reliably tagged, so a hard filter would too often leave nothing to show (see
 * prioritiseByContext, which this shares its base tag-matching rules with).
 *
 * Score per FAQ = sum over {grade, speciality/rootSpeciality, location} of that term's best match
 * (capped at EXACT_MATCH_WEIGHT per term - see scoreTermAgainstFaq) + a flat recency bonus when the
 * FAQ was updated within the last RECENCY_WINDOW_DAYS days. When no location term is active, the
 * location dimension scores against DEFAULT_LOCATION_TERM ("Australia") at DEFAULT_LOCATION_WEIGHT
 * (a small fraction of a real match - see the weight invariant above) instead of contributing
 * nothing.
 */
export const filterFaqsForContext = (
  faqs: IFrequentlyAskedQuestion[],
  { grade, speciality, rootSpeciality, location }: FaqContext,
): IFrequentlyAskedQuestion[] => {
  const explicitTerms = [grade, speciality, rootSpeciality, location]
    .filter((value): value is string => !!value)
    .map(term => ({ term, weight: EXACT_MATCH_WEIGHT }));
  // An empty-string location counts as "no location filter selected", same as undefined.
  const contextTerms: WeightedContextTerm[] = location
    ? explicitTerms
    : [...explicitTerms, { term: DEFAULT_LOCATION_TERM, weight: DEFAULT_LOCATION_WEIGHT }];

  const now = Date.now();
  const decorated = faqs.map(faq => ({ faq, score: scoreFaq(faq, contextTerms, now) }));
  // Array#sort is a stable sort (guaranteed since ES2019), so FAQs with an equal score keep their
  // original relative order rather than being shuffled.
  decorated.sort((a, b) => b.score - a.score);

  return decorated.map(({ faq }) => faq).slice(0, MAX_FAQ_ITEMS);
};
